// vessel identification — HIN (hull identification number) per vessel. the list is the union of the
// imported SAP→HIN records AND the current GRP-inspection vessels; vessel NAME/MODEL are matched from
// GRP by SAP (blank for historical SAPs not in GRP). changing an EXISTING HIN opens a reason dialog
// and notifies admins. admins can bulk-import the SAP→HIN list from Excel.

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { GrpVessel } from '@technohull/shared';
import { useGrp, useHins, useHinHistory, useImportHins, useSetHin, useSnIndex } from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { usePrefs } from '../settings/SettingsContext.js';
import { useNameResolver } from '../features/directory.js';
import { ErrorState, InlineError, InlineLoading, LoadingState, LogoSpin, Modal, OverflowActions, PageHeader } from '../components/ui.js';
import { IconChevronRight, IconDownload, IconFile, IconUpload } from '../components/icons.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { localTodayIso } from '../lib/dates.js';
import { MQ, useIsPhone, useMediaQuery } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import { exportSheet } from '../features/exportSheet.js';

interface Row { sap: string; name: string; model: string }
interface PendingEdit { sap: string; name: string; oldHin: string; newHin: string }

interface HoverInfo { sap: string; name: string; by: string; at: string; rect: DOMRect }

interface SnRef { sap: string; name: string; fileKey: string; fileName: string }

// on phones each row is a card (tbl-cards): SAP · model with the S/N file on line 1, the vessel name, then
// the HIN field full width with Save beside it. Where nothing can hover (touch) or on a phone, the HIN
// history is an inline "History" disclosure under the field instead of the floating hover card.
const HinRow = memo(function HinRow({
  row,
  stored,
  lastBy,
  lastAt,
  canWrite,
  snFile,
  inlineHist,
  hoverHist,
  onSave,
  onHover,
  onPreviewSn,
}: {
  row: Row;
  stored: string;
  lastBy: string;
  lastAt: string;
  canWrite: boolean;
  snFile?: { key: string; name: string };
  /** history as an inline disclosure (touch / phone) instead of the hover card */
  inlineHist: boolean;
  /** the last-change card on hover (a hovering pointer, and not turned off in Settings) */
  hoverHist: boolean;
  onSave: (row: Row, oldHin: string, newHin: string) => void;
  onHover: (h: HoverInfo | null) => void;
  onPreviewSn: (ref: SnRef) => void;
}) {
  const [draft, setDraft] = useState(stored);
  const [histOpen, setHistOpen] = useState(false);
  useEffect(() => setDraft(stored), [stored]); // resync after a save / import
  const changed = draft.trim() !== stored.trim();
  const showSave = canWrite && changed;
  const hover = (e: { currentTarget: HTMLElement }) =>
    onHover({ sap: row.sap, name: row.name, by: lastBy, at: lastAt, rect: e.currentTarget.getBoundingClientRect() });
  return (
    <tr>
      <td className="tc-title">{row.sap}{row.model && <span className="vid-model-inline"> · {row.model}</span>}</td>
      <td className="vid-name">{row.name || <span className="muted">-</span>}</td>
      <td className="muted tc-hide">{row.model || '-'}</td>
      <td
        className={`vid-hin${showSave ? ' has-save' : ''}${canWrite ? '' : ' tc-lbl'}`}
        data-label="HIN"
        {...(!hoverHist ? {} : {
          style: { cursor: 'help' },
          onMouseEnter: hover,
          onMouseLeave: () => onHover(null),
          // keyboard users editing the HIN get the same last-change card (focus bubbles up from the input)
          onFocus: hover,
          onBlur: () => onHover(null),
        })}
      >
        {canWrite ? (
          <input
            className="field vid-hin-in"
            value={draft}
            aria-label={`HIN for SAP ${row.sap}`}
            placeholder="HIN…"
            maxLength={60}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && changed) onSave(row, stored, draft.trim()); }}
          />
        ) : (
          <span style={{ fontFamily: 'var(--font-mono)' }}>{stored || '-'}</span>
        )}
        {inlineHist && lastBy && (
          <div className="vid-hist-wrap">
            <button type="button" className="vid-hist-btn hit-44" aria-expanded={histOpen} onClick={() => setHistOpen((o) => !o)}>
              History <span aria-hidden="true">{histOpen ? '▾' : '▸'}</span>
            </button>
            {histOpen && (
              <div className="vid-hist">
                <HinHistoryBody sap={row.sap} name={row.name} by={lastBy} at={lastAt} compact />
              </div>
            )}
          </div>
        )}
      </td>
      <td className={snFile ? 'tc-end' : 'tc-hide'} style={{ whiteSpace: 'nowrap' }}>
        {snFile && (
          <button
            className="btn btn-sm"
            title={`Preview ${snFile.name}`}
            onClick={() => onPreviewSn({ sap: row.sap, name: row.name, fileKey: snFile.key, fileName: snFile.name })}
          >
            <IconFile size={13} /> S/N
          </button>
        )}
      </td>
      <td className={showSave ? 'vid-save' : 'vid-save tc-hide'}>
        {showSave && (
          <button className="btn btn-sm btn-primary" onClick={() => onSave(row, stored, draft.trim())}>Save</button>
        )}
      </td>
    </tr>
  );
});

export function VesselIdentification() {
  const grp = useGrp();
  const hins = useHins();
  const setHin = useSetHin();
  const importHins = useImportHins();
  const snIndex = useSnIndex(); // SAP → its "SN - <sap>.txt" serial-numbers file in the Infocenter
  const [snPreview, setSnPreview] = useState<SnRef | null>(null);
  const { can } = useAuth();
  const canWrite = can('hin:write');
  const canImport = can('admin:users'); // import is admin-only
  const isPhone = useIsPhone();
  const canHover = useMediaQuery(MQ.hover);
  const inlineHist = isPhone || !canHover; // the floating history card needs a hover; touch gets a disclosure
  const prefs = usePrefs(); // Settings › Display › Cell history can turn the hover card off

  const [q, setQ] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc'); // sort by SAP
  const [pending, setPending] = useState<PendingEdit | null>(null);
  const [reason, setReason] = useState('');
  const [importMsg, setImportMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  // hover popover: last change + full HIN edit history for the hovered SAP (like Overview's CellHistoryPopover).
  const [hist, setHist] = useState<{ sap: string; name: string; by: string; at: string; top: number; left: number } | null>(null);
  const onHover = useCallback((h: HoverInfo | null) => {
    // clamped so a row low on the screen keeps the whole card (up to its 320px max) in view
    setHist(h ? { sap: h.sap, name: h.name, by: h.by, at: h.at, top: Math.min(h.rect.bottom + 4, window.innerHeight - 330), left: Math.min(h.rect.left, window.innerWidth - 300) } : null);
  }, []);

  // HIN store, keyed by SAP.
  const hinBySap = useMemo(() => {
    const m = new Map<string, { hin: string; by: string; at: string; name: string; model: string }>();
    for (const h of hins.data?.hins ?? []) m.set(h.sap, { hin: h.hin, by: h.updatedBy, at: h.updatedAt, name: h.name, model: h.model });
    return m;
  }, [hins.data]);

  // GRP vessels, keyed by SAP (for name/model matching).
  const grpBySap = useMemo(() => {
    const m = new Map<string, GrpVessel>();
    for (const v of grp.data?.vessels ?? []) if (v.sap) m.set(v.sap, v);
    return m;
  }, [grp.data]);

  // rows = every SAP that has a HIN record (from the Excel import) OR is a current GRP vessel.
  const rows = useMemo(() => {
    const saps = new Set<string>();
    for (const h of hins.data?.hins ?? []) if (h.sap) saps.add(h.sap);
    for (const v of grp.data?.vessels ?? []) if (v.sap) saps.add(v.sap);
    // name/model come from the live GRP inspection when the vessel is current; for historical hulls
    // (not in GRP) fall back to what the registry import stored on the HIN record.
    let list: Row[] = [...saps].map((sap) => {
      const g = grpBySap.get(sap);
      const rec = hinBySap.get(sap);
      return { sap, name: g?.name || rec?.name || '', model: g?.model || rec?.model || '' };
    });
    const query = fold(q.trim());
    if (query) list = list.filter((r) => hasText(query, r.sap, r.name, r.model, hinBySap.get(r.sap)?.hin));
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => a.sap.localeCompare(b.sap, undefined, { numeric: true }) * dir);
    return list;
  }, [grp.data, hins.data, q, grpBySap, hinBySap, sortDir]);

  // save: a brand-new HIN saves directly; changing an existing one opens the reason dialog.
  const onSave = useCallback((row: Row, oldHin: string, newHin: string) => {
    if (oldHin.trim() && oldHin.trim() !== newHin) {
      setReason('');
      setPending({ sap: row.sap, name: row.name, oldHin: oldHin.trim(), newHin });
    } else {
      setHin.mutate({ sap: row.sap, hin: newHin, name: row.name });
    }
  }, [setHin]);

  const confirmEdit = () => {
    if (!pending || !reason.trim()) return;
    setHin.mutate({ sap: pending.sap, hin: pending.newHin, reason: reason.trim(), name: pending.name });
    setPending(null);
    setReason('');
  };

  const onFile = async (file: File) => {
    setImportMsg('Parsing…');
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]!];
      if (!ws) { setImportMsg('The file has no sheets.'); return; }
      const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '' });
      const header = (aoa[0] ?? []).map((h) => String(h).toLowerCase());
      let sapCol = header.findIndex((h) => h.includes('sap'));
      let hinCol = header.findIndex((h) => h.includes('hin'));
      let nameCol = header.findIndex((h) => h.includes('name') || h.includes('customer'));
      let modelCol = header.findIndex((h) => h.includes('model'));
      if (sapCol < 0) sapCol = 0; // the registry export puts SAP in column A,
      if (modelCol < 0) modelCol = 1; // MODEL in column B,
      if (nameCol < 0) nameCol = 2; // customer NAME in column C,
      if (hinCol < 0) hinCol = 3; // HIN in column D.

      // The registry lists the same hull under several SAP spellings — plain (9100353), a "J"/"JS"
      // prefix (J9100353), and a "-01" unit suffix — so reduce each to its base digits. That lines the
      // record up with the app's pure-digit SAPs (Overview / GRP) AND collapses the duplicate listings.
      const baseSap = (s: string) => s.trim().replace(/^[A-Za-z]+/, '').replace(/-\d+$/, '');
      // "No HIN yet" arrives as blank, "-", or a repeated-digit placeholder (0000…, 1111…).
      const realHin = (h: string) => {
        const t = String(h).trim();
        return !t || t === '-' || /^(\d)\1+$/.test(t) ? '' : t;
      };

      // one row per base SAP, keeping the first REAL HIN together with that row's customer name + model.
      // Skip dummy customers, and never send a blank HIN — the import upserts, so a blank would overwrite
      // (wipe) a HIN already on the server.
      const best = new Map<string, { hin: string; name: string; model: string }>();
      for (let i = 1; i < aoa.length; i++) {
        const r = aoa[i] ?? [];
        const sap = baseSap(String(r[sapCol] ?? ''));
        if (!/^\d{4,}$/.test(sap)) continue; // no usable SAP digits
        const name = String(r[nameCol] ?? '').trim();
        if (/dumm/i.test(name)) continue; // dummy customer / dummies
        const hin = realHin(String(r[hinCol] ?? ''));
        if (hin && !best.has(sap)) best.set(sap, { hin, name, model: String(r[modelCol] ?? '').trim() });
      }
      const parsed = [...best].map(([sap, v]) => ({ sap, hin: v.hin, name: v.name, model: v.model }));
      if (!parsed.length) { setImportMsg('No SAP/HIN rows found in the file.'); return; }
      importHins.mutate(parsed, {
        onSuccess: (res) => setImportMsg(`Imported ${res.imported} HINs (duplicate SAPs, dummies and blank HINs skipped).`),
        onError: () => setImportMsg(''), // the reason shows in the inline error below the header
      });
    } catch {
      setImportMsg('Could not read that file.');
    }
  };

  const busy = grp.isLoading || hins.isLoading;
  const syncing = grp.isFetching || hins.isFetching;
  const flipSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
  // gate on the authoritative HIN store: rendering editable rows from GRP alone (before hins
  // loads) shows every existing HIN as blank, so a real edit is misclassified as a first-time
  // set — the client skips the reason dialog and the server rejects it with a 400. GRP stays
  // optional (names/models only).
  const ready = !!hins.data;

  // Export the visible SAP / vessel / model / HIN rows to an .xlsx (matches the import format).
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<unknown>(null);
  const exportExcel = async () => {
    const headers = ['SAP', 'Vessel', 'Model', 'HIN'];
    const data = rows.map((r) => [r.sap, r.name, r.model, hinBySap.get(r.sap)?.hin ?? '']);
    const today = localTodayIso();
    setExporting(true); setExportErr(null);
    try { await exportSheet(`Vessel Identification - HIN - ${today}.xlsx`, 'HIN', headers, data); } catch (e) { setExportErr(e); } finally { setExporting(false); }
  };

  return (
    <>
      <PageHeader title="Vessel Identification">
        <input className="field" placeholder="Search SAP / vessel / HIN" value={q} onChange={(e) => setQ(e.target.value)} />
        {/* phones: the cards have no column headers, so the SAP sort sits beside the search */}
        {isPhone && ready && (
          <button
            type="button"
            className="btn btn-sm vid-sort"
            onClick={flipSort}
            aria-label={`Sort by SAP, ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
            title="Sort by SAP"
          >
            SAP <span className="vid-sort-ic" style={{ transform: sortDir === 'asc' ? 'rotate(-90deg)' : 'rotate(90deg)' }}><IconChevronRight size={14} /></span>
          </button>
        )}
        <span className="muted page-count">{rows.length} vessel{rows.length === 1 ? '' : 's'}</span>
        {canImport && (
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = ''; }}
          />
        )}
        <OverflowActions
          title="Vessel Identification"
          items={[
            canImport && {
              label: importHins.isPending ? 'Importing…' : 'Import from Excel',
              icon: importHins.isPending ? undefined : <IconUpload size={14} />,
              onClick: () => fileRef.current?.click(),
              disabled: importHins.isPending,
            },
            {
              label: exporting ? 'Exporting…' : 'Export to Excel',
              icon: exporting ? undefined : <IconDownload size={14} />,
              onClick: () => void exportExcel(),
              disabled: exporting || !ready || rows.length === 0,
            },
            { label: syncing ? 'Syncing…' : 'Sync', icon: syncing ? <LogoSpin size={15} decorative /> : undefined, onClick: () => { void grp.refetch(); void hins.refetch(); }, disabled: syncing },
          ]}
        />
      </PageHeader>
      {importMsg && <div className="muted" style={{ marginBottom: 8 }}>{importMsg}</div>}

      {busy && <LoadingState label="Loading…" />}
      {hins.error && <ErrorState error={hins.error} onRetry={() => void hins.refetch()} />}
      {grp.error && <div className="muted" style={{ marginBottom: 8 }}>Vessel names unavailable (no GRP access) - SAP &amp; HIN still shown.</div>}
      {(setHin.error ?? importHins.error) && (
        <InlineError error={setHin.error ?? importHins.error} onDismiss={() => { setHin.reset(); importHins.reset(); }} />
      )}
      {exportErr ? <InlineError error={exportErr} onDismiss={() => setExportErr(null)} /> : null}

      {ready && (
        <div className="tbl-wrap">
          <table className="tbl tbl-cards vid-tbl">
            <thead>
              <tr>
                <th className="sortable" style={{ whiteSpace: 'nowrap' }}
                  tabIndex={0}
                  aria-sort={sortDir === 'asc' ? 'ascending' : 'descending'}
                  onClick={flipSort}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); flipSort(); } }}
                  title="Sort by SAP">
                  SAP <span style={{ display: 'inline-flex', transform: sortDir === 'asc' ? 'rotate(-90deg)' : 'rotate(90deg)' }}><IconChevronRight size={12} /></span>
                </th>
                <th>Vessel</th>
                <th>Model</th>
                <th>HIN</th>
                <th>S/N</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rec = hinBySap.get(row.sap);
                return (
                  <HinRow
                    key={row.sap}
                    row={row}
                    stored={rec?.hin ?? ''}
                    lastBy={rec?.by ?? ''}
                    lastAt={rec?.at ?? ''}
                    canWrite={canWrite}
                    snFile={snIndex.data?.index[row.sap]}
                    inlineHist={inlineHist}
                    hoverHist={!inlineHist && prefs.cellHistory}
                    onSave={onSave}
                    onHover={onHover}
                    onPreviewSn={setSnPreview}
                  />
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>No vessels.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pending && (
        <Modal
          title="Reason for changing HIN"
          onClose={() => { setPending(null); setReason(''); }}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => { setPending(null); setReason(''); }}>Cancel</button>
              <button className="btn btn-primary" disabled={!reason.trim() || setHin.isPending} onClick={confirmEdit}>Save change</button>
            </>
          }
        >
          <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
            SAP {pending.sap}{pending.name ? ` · ${pending.name}` : ''}<br />
            <span style={{ fontFamily: 'var(--font-mono)' }}>{pending.oldHin || '-'}</span> → <span style={{ fontFamily: 'var(--font-mono)' }}>{pending.newHin || '-'}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button className="btn btn-sm" onClick={() => setReason('Wrong HIN')}>Wrong HIN</button>
            <button className="btn btn-sm" onClick={() => setReason('New HIN assigned')}>New HIN assigned</button>
          </div>
          <label>
            <span className="field-label">Reason (required)</span>
            <input className="field" style={{ width: '100%' }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this HIN changing?" autoFocus />
          </label>
          <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>Admins will be notified of this change.</div>
        </Modal>
      )}

      {hist && !inlineHist && <HinHistoryPopover {...hist} />}

      {snPreview && (
        <SnPreviewModal
          sap={snPreview.sap}
          name={snPreview.name}
          fileKey={snPreview.fileKey}
          fileName={snPreview.fileName}
          onClose={() => setSnPreview(null)}
        />
      )}
    </>
  );
}

/** Preview the vessel's serial-numbers .txt (fetched inline), with a download. */
function SnPreviewModal({ sap, name, fileKey, fileName, onClose }: SnRef & { onClose: () => void }) {
  const { can } = useAuth();
  const [text, setText] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch(`/api/files/sn?key=${encodeURIComponent(fileKey)}`)
      .then((r) => { if (!r.ok) throw new Error('load failed'); return r.text(); })
      .then((t) => { if (alive) setText(t); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [fileKey]);
  return (
    <Modal
      title={`Serial numbers - SAP ${sap}${name ? ` · ${name}` : ''}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          {/* the vessel's Serial numbers page, where the file is changed (Info / Files › Infocenter) */}
          {can('production:read') && <Link className="btn" to={`/serial-numbers/${encodeURIComponent(sap)}${name ? `?name=${encodeURIComponent(name)}` : ''}`}>{can('production:write') ? 'Change' : 'Open'}</Link>}
          <a className="btn btn-primary" href={`/api/files/sn?key=${encodeURIComponent(fileKey)}&dl=1`}><IconDownload size={14} /> Download {fileName}</a>
        </>
      }
    >
      {err ? (
        <div className="muted">Could not load the file.</div>
      ) : text === null ? (
        <InlineLoading />
      ) : (
        <pre className="vid-sn-pre" style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{text}</pre>
      )}
    </Modal>
  );
}

/** floating, non-interactive card (shown on hover) with a SAP's last HIN change + full edit history. */
function HinHistoryPopover({ top, left, ...rest }: { sap: string; name: string; by: string; at: string; top: number; left: number }) {
  return (
    <div
      style={{
        position: 'fixed', top, left, zIndex: 60, width: 300, maxHeight: 320, overflowY: 'auto',
        background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)', padding: 10, pointerEvents: 'none', fontSize: 12,
      }}
    >
      <HinHistoryBody {...rest} />
    </div>
  );
}

/** a SAP's last HIN change + full edit history: the hover card's body, and the touch disclosure's
 *  (`compact` drops the heading, which the card it opens in already shows). */
function HinHistoryBody({ sap, name, by, at, compact }: { sap: string; name: string; by: string; at: string; compact?: boolean }) {
  const { data, isLoading, isError } = useHinHistory(sap);
  const nameOf = useNameResolver();
  const fmtDate = useFormatDate();
  const df = useDateFormatter();
  const entries = data?.history ?? [];
  return (
    <>
      {!compact && <div style={{ fontWeight: 700 }}>HIN change history</div>}
      {!compact && <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>SAP {sap}{name ? ` · ${name}` : ''}</div>}
      {by ? (
        <div style={{ marginBottom: entries.length ? 8 : 0 }}>
          <span className="muted" style={{ fontSize: 11 }}>Last change</span>
          <div style={{ fontWeight: 600 }}>{nameOf(by)}{at ? ` · ${fmtDate(at)}` : ''}</div>
        </div>
      ) : (
        <div className="muted">No changes recorded.</div>
      )}
      {isLoading && <InlineLoading label="Loading history…" />}
      {isError && <div className="muted">Couldn’t load the change history.</div>}
      {entries.map((e, i) => (
        <div key={i} style={{ padding: '6px 0', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontFamily: 'var(--font-mono)' }}>{e.oldHin || '-'} → {e.newHin || '-'}</div>
          {e.reason && <div className="muted" style={{ fontSize: 11 }}>“{e.reason}”</div>}
          <div className="muted" style={{ fontSize: 11 }}>{nameOf(e.by)}{e.at ? ` · ${df.dateTime(e.at)}` : ''}</div>
        </div>
      ))}
    </>
  );
}
